import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import BasePrimeForm from "./BasePrimeForm";
import AmountInput from "../../../components/Utils/AmountInput";

const PrimeNonImposable = ({ id, prime, setPrime, closeEdit }) => {
    const { t } = useTranslation();
    const [affichage, setAffichage] = useState('net');
    const [isAdvanced, setIsAdvanced] = useState(false);

    useEffect(() => {
        if (prime.category) {
            // Déterminer l'affichage basé sur la catégorie
            if (prime.category === "CBNPNI") {
                setAffichage('brut');
            } else if (prime.category === "NOIMP") {
                setAffichage('net');
            }
        }
    }, [prime.category]);

    useEffect(() => {
        // Initialiser les valeurs par défaut
        if (!prime.amount_mode) {
            setPrime(p => ({ ...p, amount_mode: 'fixed' }));
        }
        if (!prime.base_type) {
            setPrime(p => ({ ...p, base_type: 'wage' }));
        }
        if (!prime.base_rate) {
            setPrime(p => ({ ...p, base_rate: 100.0 }));
        }
        if (!prime.base_multiplier) {
            setPrime(p => ({ ...p, base_multiplier: 1.0 }));
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPrime(p => ({ ...p, [name]: value }));
    };

    const handleMontantChange = (newValue) => {
        setPrime(p => ({ ...p, valeur: newValue }));
    };

    const handleAffichageChange = ({ target: { value } }) => {
        setAffichage(value);
        updatePrimeCategory(value);
    };

    const updatePrimeCategory = (affichageType) => {
        const category = affichageType === 'brut' ? 'CBNPNI' : 'NOIMP';
        setPrime(p => ({ ...p, category }));
    };

    const handleAmountModeChange = ({ target: { value } }) => {
        setPrime(p => ({ 
            ...p, 
            amount_mode: value,
            ...(value === 'computed' ? { valeur: 0 } : {})
        }));
    };

    const handleBaseTypeChange = ({ target: { value } }) => {
        setPrime(p => ({ ...p, base_type: value }));
    };

    const handleBaseRateChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setPrime(p => ({ ...p, base_rate: value }));
    };

    const handleBaseMultiplierChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setPrime(p => ({ ...p, base_multiplier: value }));
    };

    const onBeforeSave = (primeToSave) => {
        // La catégorie est déjà définie par updatePrimeCategory
        if (!primeToSave.category) {
            primeToSave.category = affichage === 'brut' ? 'CBNPNI' : 'NOIMP';
        }
    };

    const handleModeToggle = (checked) => {
        setIsAdvanced(checked);
        if (!checked) {
            setPrime(p => ({ ...p, amount_mode: 'fixed' }));
        }
    };

    return (
        <BasePrimeForm
            id={id}
            prime={prime}
            setPrime={setPrime}
            closeEdit={closeEdit}
            title={t('primeNonImposable.title')}
            onBeforeSave={onBeforeSave}
            isAdvanced={isAdvanced}
            onAdvancedToggle={handleModeToggle}
        >
            <div className="form-control">
                <label className="label"><span className="label-text">{t('primeNonImposable.name')}</span></label>
                <input type="text" className="input input-bordered w-1/2" placeholder={t('primeNonImposable.name')} name="name" value={prime.name || ''} onChange={handleChange} required />
                <p className="mt-1 text-sm text-gray-500">{t('primeNonImposable.nameHelp')}</p>
            </div>

            {isAdvanced && (
                <div className="form-control">
                    <label className="label"><span className="label-text">{t('primeNonImposable.position')}</span></label>
                    <input type="text" className="input input-bordered w-1/2" placeholder={t('primeNonImposable.position')} name="position" value={prime.position || ''} onChange={handleChange} />
                    <p className="mt-1 text-sm text-gray-500">{t('primeNonImposable.positionHelp')}</p>
                </div>
            )}

            {isAdvanced && (
                <div className="form-control">
                    <label className="label"><span className="label-text">{t('primeNonImposable.calculationMode')}</span></label>
                    <select className="select select-bordered w-1/2" value={prime.amount_mode || 'fixed'} onChange={handleAmountModeChange} disabled={!!id}>
                        <option value='fixed'>{t('primeNonImposable.fixedAmount')}</option>
                        <option value='computed'>{t('primeNonImposable.percentage')}</option>
                    </select>
                    <p className="mt-1 text-sm text-gray-500">{t('primeNonImposable.calculationModeHelp')}</p>
                </div>
            )}

            {isAdvanced && prime.amount_mode === 'computed' && (
                <>
                    <div className="form-control">
                        <label className="label"><span className="label-text">{t('primeNonImposable.calculationBase')}</span></label>
                        <select className="select select-bordered w-1/2" value={prime.base_type || 'wage'} onChange={handleBaseTypeChange} disabled={!!id}>
                            <option value='wage'>{t('primeNonImposable.baseSalary')}</option>
                            <option value='brut'>{t('primeNonImposable.gross')}</option>
                        </select>
                        <p className="mt-1 text-sm text-gray-500">{t('primeNonImposable.calculationBaseHelp')}</p>
                    </div>
                    <div className="form-control">
                        <label className="label"><span className="label-text">{t('primeNonImposable.rate')}</span></label>
                        <input
                            type="number"
                            className="input input-bordered w-1/2"
                            value={prime.base_rate || 100.0}
                            onChange={handleBaseRateChange}
                            step="0.01"
                            min="0"
                            max="100"
                            required
                        />
                        <p className="mt-1 text-sm text-gray-500">{t('primeNonImposable.rateHelp')}</p>
                    </div>
                    <div className="form-control">
                        <label className="label"><span className="label-text">{t('primeNonImposable.multiplier')}</span></label>
                        <input
                            type="number"
                            className="input input-bordered w-1/2"
                            value={prime.base_multiplier || 1.0}
                            onChange={handleBaseMultiplierChange}
                            step="0.01"
                            min="0"
                            required
                        />
                        <p className="mt-1 text-sm text-gray-500">{t('primeNonImposable.multiplierHelp')}</p>
                    </div>
                </>
            )}

            {(!isAdvanced || prime.amount_mode !== 'computed') && (
                <div className="form-control">
                    <label className="label"><span className="label-text">{t('primeNonImposable.amount')}</span></label>
                    <AmountInput
                        value={prime.valeur}
                        onChange={handleMontantChange}
                        className="input input-bordered w-1/2"
                        required
                    />
                    <p className="mt-1 text-sm text-gray-500">{t('primeNonImposable.amountHelp')}</p>
                </div>
            )}

            <div className="form-control">
                <label className="label"><span className="label-text">{t('primeNonImposable.display')}</span></label>
                <select className="select select-bordered w-1/2" value={affichage} onChange={handleAffichageChange} disabled={!!id}>
                    <option value='net'>{t('primeNonImposable.displayNet')}</option>
                    <option value='brut'>{t('primeNonImposable.displayGross')}</option>
                </select>
                <p className="mt-2 text-sm text-gray-500">
                    <span className="text-red-500">*</span>
                    {affichage === 'brut'
                        ? ` ${t('primeNonImposable.displayGrossHelp')}`
                        : ` ${t('primeNonImposable.displayNetHelp')}`
                    }
                </p>
            </div>
        </BasePrimeForm>
    );
}

export default PrimeNonImposable;